/**
 * One saved trip: its plans, and the itinerary of the one that is chosen.
 *
 * This screen exists because a generated plan used to live only in the planner component's state. The
 * API has always stored the trip, its place selection, its variants and its days — but the client never
 * read any of it back, so leaving the planner or reloading the page lost an itinerary that was still on
 * the server, and `/trips` could not list what it had. The loop the product promises (save a place →
 * plan → *go*) ended at the plan.
 *
 * What it deliberately does not do is re-plan. `useGeneratePlans` calls the same endpoint the planner
 * does, against *this* trip id, so a draft becomes planned without a second trip being created; but a
 * trip with no places is sent to the planner's place selection instead, because the fix for "this trip
 * has nothing to plan with" is a place, not another plan.
 *
 * The plan it shows is the trip's own selection, not a mode the reader picks. It used to offer the three
 * strategies as a segmented control, and measuring that control settled the question: on a three-day Đà
 * Lạt trip with five arranged places, all three produced **identical stops, order, distance and day
 * split** — 5 điểm, 13.8 km and 19 phút of driving for every one of them — differing only in rest
 * (4.0 / 3.0 / 1.5h), which the trip's own pace already decides. Three character-identical itineraries
 * are not a choice, and the places were the user's decision in the first place, so the control and its
 * `?variant=` URL are gone. What the screen reads is the trip's `is_selected` variant, with the first
 * stored plan as the fallback for a trip whose plans were generated but never committed; a trip with no
 * plans still offers to make them. The planner keeps its own strategy chips, because there they are
 * picked before an arrangement exists — the one state where the strategies genuinely differ.
 *
 * The "Chọn phương án này" button went the same way and for the same reason: it recorded the shown plan
 * as the trip's own, but with an arrangement the three plans it offered to choose among are one plan, so
 * it was a choice between identical things. It is gone, with its state and its server write; confirming a
 * plan is the planner's review step, which has the arrangement the plan was built from on screen.
 */

import { useState } from "react";
import type { ReactElement } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";

import { FundTab } from "../components/FundTab.tsx";
import { PlanRoadmap } from "../components/PlanRoadmap.tsx";
import { copy } from "../copy.ts";
import { messageFor } from "../lib/errors.ts";
import { formatVnd } from "../lib/money.ts";
import { tripDateRange } from "../lib/tripDates.ts";
import {
  useGeneratePlans,
  useTrip,
  useTripFund,
  useTripPlan,
  useTripPlans,
} from "../lib/queries.ts";
import { useCurrentUser } from "../lib/session-context.tsx";

/** `draft` until a plan is selected — the status the server now keeps, and what this screen shows. */
function statusLabel(status: string): string {
  return status === "planned" ? copy.trips.planned : copy.trips.draft;
}

export function TripDetailScreen(): ReactElement {
  const { tripId = "" } = useParams<{ tripId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const user = useCurrentUser();
  const userId = user?.id ?? "";

  const tripQuery = useTrip(userId, tripId);
  const plansQuery = useTripPlans(userId, tripId);
  const generatePlans = useGeneratePlans(userId, tripId);
  /*
   * The fund is read here, not only inside the tab, because the header chip shows the pot from every tab
   * — the amount must not sit behind a tab. `FundTab` reads the same query key, so the two share one
   * request and one invalidation.
   */
  const fundQuery = useTripFund(userId, tripId);

  /*
   * Which plan is shown, resolved in one order: the trip's own selection, then the first stored plan.
   * The first step is what makes returning to a trip show the itinerary the user actually chose rather
   * than an arbitrary one of three; the second covers a trip whose plans were generated but never
   * committed, and the itinerary is still the thing to read — this screen offers no control for choosing
   * among plans. There is no URL variant any more: with an arrangement the strategies are identical, so
   * there is nothing for the reader to switch between.
   */
  const variants = plansQuery.data?.variants ?? [];
  const selected = variants.find((variant) => variant.is_selected) ?? null;
  const openVariantId = selected?.id ?? variants[0]?.id ?? "";
  const detailQuery = useTripPlan(userId, tripId, openVariantId);

  // The generate control's own pending state, so it is the one that shows the wait.
  const [generatingPlan, setGeneratingPlan] = useState(false);

  /*
   * Which tab is open is a URL decision (`?tab=quy`), so sending a link, reloading or coming back lands
   * on the same tab.
   */
  const activeTab: "route" | "fund" = searchParams.get("tab") === "quy" ? "fund" : "route";
  /*
   * The condition is a boolean so that the only string literals inside `className={…}` are class names.
   * A comparison written inline (`className={activeTab === "route" ? …}`) puts `"route"` there, and the
   * stylesheet test reads every quoted string in a class attribute as a class name.
   */
  const isRouteTab = activeTab === "route";
  const isFundTab = activeTab === "fund";

  function openTab(tab: "route" | "fund"): void {
    const next = new URLSearchParams(searchParams);
    if (tab === "fund") next.set("tab", "quy");
    else next.delete("tab");
    setSearchParams(next, { replace: true });
  }

  const fund = fundQuery.data ?? null;
  /*
   * The chip's words, the same in every state. A null pot is a measured zero, so it reads `0 đ` like any
   * other figure; a negative pot is a number to read and is announced as "âm …" rather than with a minus
   * sign a screen reader may skip. The figure is always present, so the button's own name says what it is.
   */
  const potVnd = fund?.pot_vnd ?? 0;
  const chipLabel =
    potVnd < 0
      ? copy.expenses.chipOpenNegative(formatVnd(Math.abs(potVnd)))
      : copy.expenses.chipOpen(formatVnd(potVnd));

  const trip = tripQuery.data?.trip ?? null;
  // The selection lives beside the trip in the response, not on it: `trip_constraint_places` is a join
  // table, and `place_ids` is what `GET /trips/:id` reports of it.
  const placeIds = tripQuery.data?.place_ids ?? [];

  if (tripQuery.isPending) {
    return (
      <div className="screen">
        <p className="state">{copy.state.loading}</p>
      </div>
    );
  }

  if (tripQuery.isError || trip === null) {
    return (
      <div className="screen">
        <div className="state">
          {/*
            A trip that is not there is a different fact from a trip that failed to load, and this
            screen says the first: `useTrip` does not retry a 404, so reaching here after a successful
            session means the id does not belong to this user any more.
          */}
          <p className="state__desc">{copy.trips.tripNotFound}</p>
          <Link className="button" to="/trips">
            {copy.trips.heading}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="screen">
      {/*
        The app bar and the screen's two tabs, as one block. The tab strip used to be a row of its own
        under this bar, which cost a whole gap plus a row of chrome above every itinerary; it now sits
        directly under the trip name, inside the same header, and the two read as one piece of furniture.
        The strip keeps the 44px tap floor, so what the merge frees is the space between the rows rather
        than the row itself.
      */}
      <header className="trip-head">
        <div className="trip-bar">
          <div className="trip-bar__text">
            <h1 className="trip-bar__title">{trip.title}</h1>
            <p className="trip-bar__sub">
              {trip.destination} · {tripDateRange(trip.start_date, trip.end_date) ?? copy.state.unknown} ·{" "}
              {statusLabel(trip.status)}
            </p>
          </div>
          <div className="trip-bar__actions">
            {/*
              The fund, as a wallet whose badge carries the pot, kept in the header on the itinerary tab
              because the balance must not live behind a tab. The badge is a figure in every state — an
              empty pot is a measured zero and reads `0 đ` — so there is no dot: the one thing the control
              has to say is a number, and the button's own name says what it opens.
            */}
            {!isFundTab && (
              <button className="headpot" type="button" aria-label={chipLabel} onClick={() => openTab("fund")}>
                <span className="material-symbols-outlined trip-bar__glyph" aria-hidden="true">
                  account_balance_wallet
                </span>
                <span className="headpot__badge">{formatVnd(potVnd)}</span>
              </button>
            )}
            {/*
              Reading a trip and finding something wrong with it is the moment to fix it, so the door is here as
              well as on the list. It goes to the planner, which owns every field a trip has — a second form for
              the same data would answer differently the first time one of them changed.
            */}
            <Link className="trip-bar__icon" to={`/trips/${tripId}/edit`} aria-label={copy.trips.edit}>
              <span className="material-symbols-outlined trip-bar__glyph" aria-hidden="true">
                edit
              </span>
            </Link>
          </div>
        </div>

        {/*
          The two destinations, as fixed tabs: the screen's own tab list, and the only one on it. The
          fund tab reads just its name — the count lives in the three-cell cluster and on the filter tabs
          below, so a third copy on the pill was one figure in three places.
        */}
        <div className="chips trip-tabs" role="tablist" aria-label={copy.expenses.tabsLabel}>
          <button
            className={isRouteTab ? "chip chip--on" : "chip"}
            type="button"
            role="tab"
            aria-selected={isRouteTab}
            onClick={() => openTab("route")}
          >
            {copy.expenses.tabItinerary}
          </button>
          <button
            className={isFundTab ? "chip chip--on" : "chip"}
            type="button"
            role="tab"
            aria-selected={isFundTab}
            onClick={() => openTab("fund")}
          >
            {copy.expenses.tabFund}
          </button>
        </div>
      </header>

      {activeTab === "fund" && fundQuery.isPending && <p className="state">{copy.state.loading}</p>}
      {activeTab === "fund" && fundQuery.isError && (
        <div className="state">
          <p className="state__desc">{messageFor(fundQuery.error)}</p>
          <button type="button" className="button" onClick={() => void fundQuery.refetch()}>
            {copy.state.retry}
          </button>
        </div>
      )}
      {activeTab === "fund" && fund !== null && (
        <FundTab
          userId={userId}
          tripId={tripId}
          startDate={trip.start_date}
          endDate={trip.end_date}
          fund={fund}
        />
      )}

      {activeTab === "route" && (
        <>
      {plansQuery.isPending && <p className="state">{copy.state.loading}</p>}

      {plansQuery.isSuccess && variants.length === 0 && (
        <div className="state">
          <p className="state__desc">{copy.trips.noPlansYet}</p>
          {/*
            Two different gaps, two different doors — the same split the planner makes. Generating plans
            for a trip with no places is a 422, so this only offers the button when there is something to
            plan with, and otherwise points at where places are chosen.
          */}
          {placeIds.length > 0 ? (
            <button
              type="button"
              className="button button--primary"
              disabled={generatePlans.isPending}
              onClick={() => {
                setGeneratingPlan(true);
                generatePlans.mutate(undefined, { onSettled: () => setGeneratingPlan(false) });
              }}
            >
              {generatingPlan ? copy.state.loading : copy.trips.generatePlans}
            </button>
          ) : (
            <Link className="button button--primary" to="/places">
              {copy.trips.savePlaces}
            </Link>
          )}
          {generatePlans.isError && (
            <p className="state__desc" role="alert">
              {messageFor(generatePlans.error)}
            </p>
          )}
        </div>
      )}

      {/*
        No plan control here, and that is deliberate.

        The segmented mode switch went first: measured on a three-day trip with an arrangement, the three
        strategies produced identical stops, order, distance and day split, differing only in rest time,
        which the pace decides, so a control offering three identical itineraries was not a choice. The
        "Chọn phương án này" button that recorded the shown plan followed it for the same reason — with an
        arrangement, the three plans it offered to choose among are one plan.

        A trip whose plans were generated but never committed still renders its first stored plan
        (`openVariantId` above), and there is nothing to press: confirming a plan is the planner's review
        step, where the arrangement that produced it is on screen. The route tab reads; it does not write.
      */}

      {detailQuery.isPending && openVariantId !== "" && <p className="state">{copy.state.loading}</p>}

      {detailQuery.isError && (
        <div className="state">
          <p className="state__desc">{messageFor(detailQuery.error)}</p>
          <button type="button" className="button" onClick={() => void detailQuery.refetch()}>
            {copy.state.retry}
          </button>
        </div>
      )}

      {/*
        The itinerary, and nothing restating it. `PlanRoadmap` renders the plan's figures, its days, its
        road and its stops — including the rest hours that distinguish two variants sharing a route — so
        a summary line above it was the same numbers a second time, which is what the screenshot showed.
      */}
      {detailQuery.data && (
        <PlanRoadmap
          key={detailQuery.data.variant.id}
          detail={detailQuery.data}
          startDate={trip.start_date}
        />
      )}
        </>
      )}
    </div>
  );
}
